import { useAuth } from '../contexts/AuthContext';

/**
 * V6.5 Hybrid RBAC - Permission checking hook
 *
 * Provides permission-based access control methods.
 * Complements useRoleCheck with granular permission checks.
 *
 * Example usage:
 * ```tsx
 * const { hasPermission } = usePermissions();
 * if (hasPermission('users.delete')) {
 *   // Show delete button
 * }
 * ```
 */
export function usePermissions() {
  const { permissions } = useAuth();

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permissionCode: string): boolean => {
    return permissions.includes(permissionCode);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    return permissionCodes.some(code => permissions.includes(code));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    return permissionCodes.every(code => permissions.includes(code));
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}
